import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { AdminAlert, AdminPageHeader } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { brandSiteDomainApiPath } from "@/lib/brand-site-routing";
import { cn } from "@/lib/utils";
import { useBrandSiteWorkbench } from "@/pages/brand-site/BrandSiteSiteLayout";

type DomainPayload = {
  host: string;
  status: string;
  error_message: string | null;
  cname_name: string;
  cname_target: string;
};

type BrandSiteDomainResponse = {
  domain: DomainPayload | null;
  edge_cname_target: string;
};

function domainStatusLabel(status: string, errorMessage: string | null) {
  switch (status) {
    case "pending_dns":
      return "等待验证";
    case "issuing_cert":
      return "签发证书中";
    case "active":
      return "已 HTTPS";
    case "error":
      return errorMessage || "异常";
    default:
      return status;
  }
}

/** 域名栏：HTTP 挑战验证所有权，通过后启用 HTTPS */
export default function BrandSiteDomainPage() {
  const { brandId } = useParams();
  const { accountId, brands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const { currentSite } = useBrandSiteWorkbench();

  const [host, setHost] = useState("");
  const [domain, setDomain] = useState<DomainPayload | null>(null);
  const [edgeTarget, setEdgeTarget] = useState("app.c456.com");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const path = brandSiteDomainApiPath(accountId, brandId!, currentSite.id);

  const applyPayload = useCallback((payload: BrandSiteDomainResponse) => {
    setDomain(payload.domain);
    setHost(payload.domain?.host ?? "");
    setEdgeTarget(payload.edge_cname_target || "app.c456.com");
  }, []);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await api.fetchForAccount<BrandSiteDomainResponse>(accountId, path);
      applyPayload(payload);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法加载域名");
      setDomain(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, brandId, path, applyPayload]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveHost() {
    setSaving(true);
    setError(null);
    try {
      const payload = await api.fetchForAccount<BrandSiteDomainResponse>(accountId, path, {
        method: "PATCH",
        body: JSON.stringify({ host: host.trim(), brand_site_id: currentSite.id }),
      });
      applyPayload(payload);
      toast.success(host.trim() ? "已保存域名" : "已解除绑定");
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "保存失败";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function probe() {
    if (!domain) return;
    setProbing(true);
    setError(null);
    try {
      const payload = await api.fetchForAccount<BrandSiteDomainResponse>(
        accountId,
        brandSiteDomainApiPath(accountId, brandId!, currentSite.id, "probe"),
        { method: "POST" },
      );
      applyPayload(payload);
      const status = payload.domain?.status;
      if (status === "active") {
        toast.success("域名已 HTTPS 就绪");
      } else if (status === "issuing_cert") {
        toast.success("已验证，正在启用 HTTPS");
      } else {
        toast.message(payload.domain?.error_message || domainStatusLabel(status ?? "", null));
      }
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "检测失败";
      setError(message);
      toast.error(message);
    } finally {
      setProbing(false);
    }
  }

  async function copyText(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`已复制${label}`);
    } catch {
      toast.error("复制失败");
    }
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="域名"
        description={
          brand
            ? `把「${brand.name}」绑到自己买的域名。把域名解析到接入地址（或经反代把 80/443 转到该地址），验证通过后访客用你的域名打开同一座官网。`
            : "绑定自己的域名"
        }
      />

      {loading ? <p className="text-sm text-muted-foreground">加载中…</p> : null}
      {error ? <AdminAlert>{error}</AdminAlert> : null}

      <div className="space-y-4 rounded-lg border border-border bg-card px-5 py-4">
        <div className="space-y-2">
          <Label htmlFor="brand_site_host">域名</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="brand_site_host"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              disabled={saving}
              placeholder="www.example.com"
              autoComplete="off"
              className="font-mono text-sm"
            />
            <Button type="button" variant="outline" disabled={saving} onClick={() => void saveHost()}>
              {saving ? "保存中…" : "保存"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            还没发布也可以先绑。解析或反代未就绪前，这个域名暂时打不开。清空再保存即取消绑定。
          </p>
        </div>

        {domain ? (
          <>
            <div className="space-y-2 rounded-md border border-border bg-muted/20 px-3 py-3">
              <p className="text-sm text-muted-foreground">
                把域名解析到接入地址，或经反代把 80/443 转到该地址，再点「检测域名」。若使用 CNAME，可把{" "}
                <code className="font-mono text-xs">{domain.cname_name}</code> 指向下方建议值。
              </p>
              <div className="space-y-1">
                <p className="text-sm font-medium">建议接入地址</p>
                <div className="flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1.5">
                  <code className="min-w-0 flex-1 truncate font-mono text-xs">{edgeTarget}</code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0"
                    aria-label="复制建议接入地址"
                    onClick={() => void copyText("建议接入地址", edgeTarget)}
                  >
                    <Copy className="size-3.5" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium">状态</p>
              <span
                className={cn(
                  "inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium",
                  domain.status === "active"
                    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {domainStatusLabel(domain.status, domain.error_message)}
              </span>
              {domain.error_message && domain.status !== "error" ? (
                <p className="text-xs text-muted-foreground">{domain.error_message}</p>
              ) : null}
            </div>

            <Button type="button" disabled={probing} onClick={() => void probe()}>
              {probing ? "检测中…" : "检测域名"}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
